import { useTheme } from '@repo/theme';
import { Banner } from 'component/banner';
import { Pad, VertBox } from 'component/basics';
import { InformationalModal } from 'component/modal/informational-modal';
import { BulletText, RichText } from 'component/richtext';
import { Heading, Paragraph } from 'component/text';
import { GUIDELINES, PARENT_GUIDELINES } from 'util/community-guidelines';
import type { TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

export type CommunityGuidelinesModalProps = {
    onClose: () => void;
};

export function CommunityGuidelinesModal({ onClose }: CommunityGuidelinesModalProps) {
    return (
        <InformationalModal
            content={<CommunityGuidelinesContent />}
            headingLabel={t('community_guidelines.modal_heading', 'Community Guidelines and Norms')}
            onClose={onClose}
        />
    );
}

function GuidelineBanner({ headingLabel, rules }: { headingLabel: TLabel; rules: TLabel[] }) {
    const { colors } = useTheme();
    return (
        <Banner color={colors.colorSurfaceBannerInfo}>
            <Heading color={colors.colorTextSecondaryOnBanner} label={headingLabel} type='small' />
            <Pad size={8} />
            {rules.map((rule) => (
                <BulletText color={colors.colorTextSecondaryOnBanner} key={rule.key ?? rule.defaultText} label={rule} />
            ))}
        </Banner>
    );
}

function CommunityGuidelinesContent() {
    return (
        <VertBox gap={32}>
            <VertBox gap={16} wide>
                <Paragraph
                    label={t(
                        'community_guidelines.modal_intro',
                        'We strive to inspire real, respectful, and open conversations in this space. Everyone is welcome to share their experiences; exchange and debate perspectives; and ask questions in the spirit of kindness, connection, and curiosity.',
                    )}
                />
                <Paragraph label={t('community_guidelines.guidelines_intro', 'Our community guidelines are:')} />
                <GuidelineBanner
                    headingLabel={PARENT_GUIDELINES.NO_HARMFUL_CONTENT}
                    rules={[GUIDELINES.NO_HARM, GUIDELINES.RESPECT_PRIVACY, GUIDELINES.NO_ILLEGAL, GUIDELINES.NO_LABELS]}
                />
                <GuidelineBanner
                    headingLabel={PARENT_GUIDELINES.NO_DISRUPTIVE_BEHAVIOR}
                    rules={[GUIDELINES.NO_SPAM, GUIDELINES.OFF_TOPIC]}
                />
                <GuidelineBanner headingLabel={PARENT_GUIDELINES.SUPPORT_YOUR_CLAIMS} rules={[GUIDELINES.BACKUP_CLAIMS]} />
            </VertBox>
            <VertBox gap={16} wide>
                <Paragraph label={t('community_guidelines.norms_intro', 'Our community norms are:')} />
                <GuidelineBanner
                    headingLabel={PARENT_GUIDELINES.ENGAGE_RESPECTFULLY}
                    rules={[GUIDELINES.NO_UNCIVIL, GUIDELINES.NO_PERSONAL_ATTACKS, GUIDELINES.NO_INSULTS, GUIDELINES.BE_CONSTRUCTIVE]}
                />
            </VertBox>
            <Paragraph
                label={t(
                    'community_guidelines.moderation_explanation',
                    'This space is designed for more thoughtful conversation than typical internet comments. Our moderation process helps maintain that standard while respecting your voice and perspective.',
                )}
            />
            <VertBox gap={16}>
                <Heading
                    label={t(
                        'community_guidelines.what_happens_heading',
                        'What happens when your contribution violates the community guidelines or norms?',
                    )}
                    type='small'
                />
                <RichText
                    label={t(
                        'community_guidelines.harmful_content_explanation',
                        '**Harmful content** that violates our community guidelines will be blocked from posting. If that determination has been made by AI, you can appeal your post to a human moderator for review.',
                    )}
                />
                <RichText
                    label={t(
                        'community_guidelines.constructive_content_explanation',
                        '**Comments that could be more constructive** may receive suggestions on how to be more consistent with our community norms. You may have the choice to revise your comment or proceed with posting as written. Comments posted without revisions after receiving suggestions may be ranked lower in the feed to prioritize more constructive contributions.',
                    )}
                />
            </VertBox>
        </VertBox>
    );
}
